/**
 * FilterRowsEditor — a structured AND-list of `{column, op, value}` predicates
 * over a table's columns, matching the backend `DataRowService` filter engine
 * (NOT a JS expression: these compile to SQL WHERE clauses server-side). Used by
 * the rollup column's child-row filters (P1.S2); reusable for any SQL-predicate
 * surface (RLS policies, page filters). Admin-only surface → native `<select>`
 * is acceptable here (operator console).
 */
import { Plus, X } from "lucide-react";

export interface FilterRow {
  column: string;
  op: string;
  value?: unknown;
}

/** Operators the backend filter engine accepts (mirror of `rows._FILTER_OPS`,
 *  the designer-surfaced subset). `is_null`/`not_null` take no value. */
export const FILTER_OPS: { value: string; label: string }[] = [
  { value: "eq", label: "= equals" },
  { value: "ne", label: "≠ not equals" },
  { value: "gt", label: "> greater than" },
  { value: "gte", label: "≥ greater or equal" },
  { value: "lt", label: "< less than" },
  { value: "lte", label: "≤ less or equal" },
  { value: "contains", label: "contains" },
  { value: "is_null", label: "is empty" },
  { value: "not_null", label: "is not empty" },
];

const NO_VALUE = new Set(["is_null", "not_null"]);

const SEL =
  "block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm disabled:opacity-50";
const INP = "min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs";

export function FilterRowsEditor({
  columns,
  value,
  onChange,
  disabled,
}: {
  /** Selectable column names (the child table's data columns). */
  columns: string[];
  value: FilterRow[];
  onChange: (rows: FilterRow[]) => void;
  disabled?: boolean;
}) {
  const setRow = (i: number, patch: Partial<FilterRow>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () =>
    onChange([...value, { column: columns[0] ?? "", op: "eq", value: "" }]);
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));

  return (
    <div className="space-y-2">
      {value.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <select
            className={SEL}
            value={r.column}
            disabled={disabled}
            onChange={(e) => setRow(i, { column: e.target.value })}
          >
            {!columns.includes(r.column) && <option value={r.column}>{r.column || "—"}</option>}
            {columns.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            className={SEL}
            value={r.op}
            disabled={disabled}
            onChange={(e) =>
              setRow(i, { op: e.target.value, ...(NO_VALUE.has(e.target.value) ? { value: undefined } : {}) })
            }
          >
            {FILTER_OPS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {!NO_VALUE.has(r.op) && (
            <input
              className={INP}
              value={String(r.value ?? "")}
              placeholder="value"
              disabled={disabled}
              onChange={(e) => setRow(i, { value: e.target.value })}
            />
          )}
          <button
            type="button"
            aria-label="Remove filter"
            onClick={() => remove(i)}
            className="shrink-0 rounded p-1 text-muted-foreground hover:text-danger"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={disabled || columns.length === 0}
        className="flex items-center gap-1 self-start rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-foreground/5 disabled:opacity-50"
      >
        <Plus className="h-3 w-3" /> Add filter
      </button>
    </div>
  );
}
